import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import { overlayBackdropStyle } from './OverlayPanel';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { apiService } from '../services/api';
import { useBusinessData } from '../hooks/useBusinessData';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import type { User, Department } from '../store/businessStore';
import { useThemedStyles } from '../hooks/useThemedStyles';

const MOBILE_HEADER_OFFSET = 72;

interface Assignment {
  _id?: string;
  assignedTo?:
    | string
    | {
        _id: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }
    | null;
  departmentId?:
    | string
    | {
        _id: string;
        name?: string;
        description?: string;
      }
    | null;
}

interface UserAssignmentModalProps {
  visible: boolean;
  onClose: () => void;
  contactId: string;
  parentContactId?: string;
  contactName: string;
  currentAssignment?: Assignment | null;
  onAssignmentChange: (assignmentData: { type: 'user' | 'department'; id: string }) => void;
  mode?: 'assign' | 'select';
  onSelection?: (selection: { type: 'user' | 'department'; id: string }) => void;
  titleOverride?: string;
  subtitleOverride?: string;
}

export const UserAssignmentModal: React.FC<UserAssignmentModalProps> = ({
  visible,
  onClose,
  contactId,
  parentContactId,
  contactName,
  currentAssignment,
  onAssignmentChange,
  mode = 'assign',
  onSelection,
  titleOverride,
  subtitleOverride,
}) => {
  const styles = useThemedStyles(getStyles);
  const isSelectionMode = mode === 'select';
  const { locale } = useLocale();
  const t = createTranslator(locale);
  
  // Use central business store for users and departments
  const {
    users: storeUsers,
    departments: storeDepartments,
    isLoadingUsers,
    isLoadingDepartments,
    refreshUsers,
    refreshDepartments
  } = useBusinessData();

  const businessRole = useAppStore(state => state.businessRole);
  const canAssignAdmins = businessRole === 'owner' || businessRole === 'admin';
  
  // Filter users to exclude owner/admin unless current user is owner/admin
  const users = storeUsers.filter(u => {
    if (canAssignAdmins) return true;
    const role = (u.role || '').toLowerCase();
    return role !== 'owner' && role !== 'admin';
  });
  const departments = storeDepartments;
  
  const [assignmentRecords, setAssignmentRecords] = useState<Assignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const effectiveContactId = parentContactId || contactId;

  // Fetch assignments when modal opens (users/departments handled by hook)
  useEffect(() => {
    if (visible) {
      fetchAssignments();
    }
  }, [visible, effectiveContactId]);

  const fetchAssignments = async () => {
    setAssignmentsLoading(true);
    try {
      const cacheBuster = Date.now();
      console.log('🔍 [UserAssignmentModal] Fetching assignments for contactId:', effectiveContactId);
      const response = await apiService.authenticatedRequest(
        `/api/assignments/target/omni_contact/${effectiveContactId}?t=${cacheBuster}`,
        { method: 'GET' }
      );

      let responseData = (response as any).data ?? (response as any).Data;
      console.log('📦 [UserAssignmentModal] Fetch response:', { statusCode: (response as any).statusCode, dataLength: Array.isArray(responseData) ? responseData.length : 0, data: responseData });
      
      if (!Array.isArray(responseData) || responseData.length === 0) {
        console.log('🔄 [UserAssignmentModal] No omni_contact assignments, trying legacy whatsapp_contact...');
        const legacyResponse = await apiService.authenticatedRequest(
          `/api/assignments/target/whatsapp_contact/${effectiveContactId}?t=${cacheBuster}`,
          { method: 'GET' }
        );
        responseData = (legacyResponse as any).data ?? (legacyResponse as any).Data;
        console.log('📦 [UserAssignmentModal] Legacy fetch response:', { statusCode: (legacyResponse as any).statusCode, dataLength: Array.isArray(responseData) ? responseData.length : 0 });
      }

      if (Array.isArray(responseData)) {
        console.log('✅ [UserAssignmentModal] Setting assignment records:', responseData.length);
        setAssignmentRecords(responseData);
        return;
      }

      if (currentAssignment) {
        console.log('⚠️ [UserAssignmentModal] Using currentAssignment fallback');
        setAssignmentRecords([currentAssignment]);
      }
    } catch (error) {
      console.error('❌ [UserAssignmentModal] Error fetching assignments:', error);
      if (currentAssignment) {
        setAssignmentRecords([currentAssignment]);
      }
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const getIdValue = (value: Assignment['assignedTo'] | Assignment['departmentId']) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value._id || null;
  };

  // Find active assignments (backend already filters out completed/cancelled/locked)
  const findUserAssignment = (userId: string) => {
    return assignmentRecords.find(a => getIdValue(a.assignedTo) === userId);
  };

  // Find active department assignments (backend already filters out completed/cancelled/locked)
  const findDepartmentAssignment = (departmentId: string) => {
    return assignmentRecords.find(a => getIdValue(a.departmentId) === departmentId);
  };

  const handleAssignUser = async (userId: string) => {
    setAssigning(true);
    try {
      const existingAssignment = findUserAssignment(userId);
      console.log('🔄 [UserAssignmentModal] Toggling user assignment:', {
        contactId,
        userId,
        isAssigned: !!existingAssignment,
      });

      if (existingAssignment?._id) {
        console.log('🗑️ [UserAssignmentModal] Deleting existing assignment:', existingAssignment._id);
        await apiService.authenticatedRequest(`/api/assignments/${existingAssignment._id}`, {
          method: 'DELETE'
        });
        setAssignmentRecords(prev => prev.filter(a => a._id !== existingAssignment._id));
      } else {
        const businessId = apiService.getBusinessIdFromToken();
        console.log('✏️ [UserAssignmentModal] Creating new assignment:', { contactId: effectiveContactId, userId, businessId });
        const response = await apiService.authenticatedRequest('/api/assignments', {
          method: 'POST',
          body: JSON.stringify({
            businessId: businessId,
            targetType: 'omni_contact',
            targetId: effectiveContactId,
            assignedTo: userId,
            assignmentType: 'contact',
            title: `Contact Assignment - ${contactName}`,
            description: `Contact assigned via mobile app`,
            status: 'active',
            priority: 'normal'
          })
        });

        const createdAssignment = (response as any).data ?? (response as any).Data;
        console.log('📦 [UserAssignmentModal] Creation response:', { statusCode: (response as any).statusCode, createdId: createdAssignment?._id });
        if (createdAssignment) {
          setAssignmentRecords(prev => [createdAssignment, ...prev]);
        }
      }

      onAssignmentChange({ type: 'user', id: userId });
    } catch (error: any) {
      console.error('❌ [UserAssignmentModal] Error assigning user:', error);
      alert(`${t('common.error')}: ${error.message || t('assignment.modal.assignFailed')}`);
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignDepartment = async (departmentId: string) => {
    setAssigning(true);
    try {
      const existingAssignment = findDepartmentAssignment(departmentId);
      console.log('🔄 [UserAssignmentModal] Toggling department assignment:', {
        contactId,
        departmentId,
        isAssigned: !!existingAssignment,
      });

      if (existingAssignment?._id) {
        await apiService.authenticatedRequest(`/api/assignments/${existingAssignment._id}`, {
          method: 'DELETE'
        });
        setAssignmentRecords(prev => prev.filter(a => a._id !== existingAssignment._id));
      } else {
        const businessId = apiService.getBusinessIdFromToken();
        const department = departments.find(d => d._id === departmentId);
        const response = await apiService.authenticatedRequest('/api/assignments', {
          method: 'POST',
          body: JSON.stringify({
            businessId: businessId,
            targetType: 'omni_contact',
            targetId: effectiveContactId,
            departmentId: departmentId,
            assignmentType: 'contact',
            title: `Contact Assignment - ${department?.name || 'Department'}`,
            description: `Contact assigned to department via mobile app`,
            status: 'active',
            priority: 'normal'
          })
        });

        const createdAssignment = (response as any).data ?? (response as any).Data;
        if (createdAssignment) {
          setAssignmentRecords(prev => [createdAssignment, ...prev]);
        }
      }

      onAssignmentChange({ type: 'department', id: departmentId });
    } catch (error: any) {
      console.error('❌ [UserAssignmentModal] Error assigning department:', error);
      alert(`${t('common.error')}: ${error.message || t('assignment.modal.assignFailed')}`);
    } finally {
      setAssigning(false);
    }
  };

  const getUserInitials = (user: User) => {
    return `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  };

  const getUserFullName = (user: User) => {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  };

  const isUserAssigned = (userId: string) => {
    return !!findUserAssignment(userId);
  };

  const isDepartmentAssigned = (departmentId: string) => {
    return !!findDepartmentAssignment(departmentId);
  };

  const handleSelection = (selection: { type: 'user' | 'department'; id: string }) => {
    onSelection?.(selection);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity 
        style={styles.overlay} 
        activeOpacity={1} 
        onPress={onClose}
      >
        <TouchableOpacity 
          style={styles.modalContainer} 
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>{titleOverride || t('assignment.modal.assignContact')}</Text>
              <Text style={styles.subtitle}>{subtitleOverride || contactName}</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text.primary} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView style={styles.content}>
            {assignmentsLoading && (
              <View style={styles.loadingInline}>
                <ActivityIndicator size="small" color={colors.primary} />
                <Text style={styles.loadingInlineText}>{t('assignment.modal.loadingAssignments')}</Text>
              </View>
            )}

            <View style={styles.sectionHeader}>
              <View style={styles.sectionTitleRow}>
                <Ionicons name="person" size={16} color={colors.text.secondary} />
                <Text style={styles.sectionTitle}>{t('assignment.modal.users')}</Text>
              </View>
              <Text style={styles.sectionSubtitle}>{t('assignment.modal.selectUsers')}</Text>
            </View>

            {isLoadingUsers ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>{t('assignment.modal.loadingUsers')}</Text>
              </View>
            ) : users.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Ionicons name="person-outline" size={48} color={colors.text.disabled} />
                <Text style={styles.emptyText}>{t('assignment.modal.noUsers')}</Text>
              </View>
            ) : (
              users.map((user) => (
                <TouchableOpacity
                  key={user._id}
                  style={[
                    styles.itemContainer,
                    !isSelectionMode && isUserAssigned(user._id) && styles.itemContainerActive
                  ]}
                  onPress={() => (
                    isSelectionMode
                      ? handleSelection({ type: 'user', id: user._id })
                      : handleAssignUser(user._id)
                  )}
                  disabled={assigning && !isSelectionMode}
                >
                  <View style={styles.itemAvatar}>
                    <Text style={styles.itemAvatarText}>
                      {getUserInitials(user)}
                    </Text>
                  </View>
                  
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{getUserFullName(user)}</Text>
                    <Text style={styles.itemEmail}>{user.email}</Text>
                    {user.role && (
                      <Text style={styles.itemRole}>{user.role}</Text>
                    )}
                  </View>

                  {!isSelectionMode && isUserAssigned(user._id) && (
                    <Ionicons name="checkmark-circle" size={24} color={colors.success} />
                  )}
                </TouchableOpacity>
              ))
            )}

            <View style={styles.sectionDivider} />

            <View style={styles.sectionHeader}>
              <View style={styles.sectionTitleRow}>
                <Ionicons name="people" size={16} color={colors.text.secondary} />
                <Text style={styles.sectionTitle}>{t('assignment.modal.departments')}</Text>
              </View>
              <Text style={styles.sectionSubtitle}>{t('assignment.modal.selectDepartments')}</Text>
            </View>

            {isLoadingDepartments ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>{t('assignment.modal.loadingDepartments')}</Text>
              </View>
            ) : departments.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Ionicons name="people-outline" size={48} color={colors.text.disabled} />
                <Text style={styles.emptyText}>{t('assignment.modal.noDepartments')}</Text>
              </View>
            ) : (
              departments.map((department) => (
                <TouchableOpacity
                  key={department._id}
                  style={[
                    styles.itemContainer,
                    !isSelectionMode && isDepartmentAssigned(department._id) && styles.itemContainerActive
                  ]}
                  onPress={() => (
                    isSelectionMode
                      ? handleSelection({ type: 'department', id: department._id })
                      : handleAssignDepartment(department._id)
                  )}
                  disabled={assigning && !isSelectionMode}
                >
                  <View style={styles.departmentIcon}>
                    <Ionicons name="people" size={20} color={colors.white} />
                  </View>
                  
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{department.name}</Text>
                    {department.description && (
                      <Text style={styles.itemDescription}>{department.description}</Text>
                    )}
                  </View>

                  {!isSelectionMode && isDepartmentAssigned(department._id) && (
                    <Ionicons name="checkmark-circle" size={24} color={colors.success} />
                  )}
                </TouchableOpacity>
              ))
            )}
          </ScrollView>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    ...overlayBackdropStyle(),
    justifyContent: Platform.OS === 'web' ? 'center' : 'flex-start',
    alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
    padding: Platform.OS === 'web' ? spacing.lg : 0,
    paddingTop: Platform.OS === 'web' ? spacing.lg : 0,
    ...(Platform.OS !== 'web' && {
      position: 'absolute',
      top: MOBILE_HEADER_OFFSET,
      left: 0,
      right: 0,
      bottom: 0,
    }),
  },
  modalContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: Platform.OS === 'web' ? 16 : 0,
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 500 : undefined,
    maxHeight: Platform.OS === 'web' ? '80%' : undefined,
    height: Platform.OS === 'web' ? undefined : undefined,
    flex: Platform.OS === 'web' ? undefined : 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  closeButton: {
    padding: spacing.xs,
  },
  content: {
    padding: spacing.lg,
  },
  loadingInline: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  loadingInlineText: {
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  sectionHeader: {
    marginBottom: spacing.sm,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  sectionSubtitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.lg,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 2,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 2,
  },
  emptyText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.background.secondary,
    borderRadius: 12,
    marginBottom: spacing.sm,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  itemContainerActive: {
    borderColor: colors.success,
    backgroundColor: (typeof colors.success === 'string' ? colors.success : String(colors.success)) + '10',
  },
  itemAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  itemAvatarText: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.white,
  },
  departmentIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: 2,
  },
  itemEmail: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginBottom: 2,
  },
  itemDescription: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginBottom: 2,
  },
  itemRole: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.disabled,
    textTransform: 'capitalize',
  },
});
